import React, { useState, useEffect, useCallback } from 'react'
import { Container } from 'typescript-ioc/es5'
import { DomainDto } from '../../dtos/domain'
import { AccountDto } from '../../dtos/account.dto'
import { AppState } from '../../stores/app'
import { AccountService, AppService, DomainService, UserService } from '../../services'
import { getClassNames } from '../../_utils/classnames'
import { DomainDetailsWell } from './domain-details-well'
import { DomainServerWell } from './domain-server-well'
import SendIntegrationsWell from './domain-send-integrations-well'
import { asCaslSubject } from '../../stores/app-ability'
import { SubjectEntity } from '../../enums/ability-entity.enum'
import { AbilityAction } from '../../enums/ability-action.enum'
import { withRouter } from 'react-router-dom'
import { RouteComponentProps } from 'react-router'
import { PlatformPreferencesWell } from '@pushly/aqe/lib/components/platform-preferences-well/platform-preferences-well'
import { PlatformUserContext } from '@pushly/aqe/lib/providers/user-preferences-provider/internal/index'
import { DeliveryChannel } from '@pushly/models/lib/enums/delivery-channels.enum'
import { ConfigProvider, Flex } from 'antd'
import { VERTICAL_WELL_GAP } from '../../theme/constants'

import './style/domain-data-form.scss'

interface IDomainDataForm extends RouteComponentProps {
    className?: string
    domainId?: number
}

const DomainDataForm: React.FC<IDomainDataForm> = (props) => {
    const [state, setState] = useState<{
        domain?: DomainDto
        account?: AccountDto
    }>({})

    // Service dependencies
    const appState = Container.get(AppState)
    const domainService = Container.get(DomainService)
    const userService = Container.get(UserService)
    const userContext = React.useContext(PlatformUserContext)

    // Computed values
    const isLoading = (!!props.domainId && !state.domain && !state.account) || userContext.loading

    // Methods
    const handleDomainUpdated = useCallback(
        async (updates: Partial<DomainDto>) => {
            setState((prevState) => ({
                ...prevState,
                domain: {
                    ...prevState.domain,
                    ...updates,
                } as DomainDto,
            }))

            await appState.fetchUserDependencies()
        },
        [appState],
    )

    const fetchRequestedDomain = useCallback(async () => {
        const { ok, data: domain } = await domainService.fetchById(props.domainId!, {
            showLoadingScreen: false,
            cancellationKey: 'ddf-fetch',
        })

        if (ok) {
            // internal type check
            // all internal users should see creator/updator info on domains
            if (appState.currentUser?.isInternalUser) {
                const creatorIdentity = asCaslSubject(SubjectEntity.USER, { id: domain.createdBy })
                const updaterIdentity = asCaslSubject(SubjectEntity.USER, { id: domain.updatedBy })
                const canReadCreator = appState.abilityStore.can(AbilityAction.READ, creatorIdentity)
                const canReadUpdater = appState.abilityStore.can(AbilityAction.READ, updaterIdentity)

                const authorReqs: Array<Promise<any>> = []
                const hasUpdateAuthor = !!domain.updatedBy
                const sameAuthor = domain.createdBy === domain.updatedBy

                if (!domain.createdByUsername && canReadCreator) {
                    authorReqs.push(
                        userService.fetchUserById(domain.createdBy, { muteErrors: true }).then((user) => {
                            if (user) {
                                domain.createdByUsername = user.name
                                if (hasUpdateAuthor && sameAuthor) {
                                    domain.updatedByUsername = user.name
                                }
                            }
                        }),
                    )
                }

                if (!domain.updatedByUsername && hasUpdateAuthor && !sameAuthor && canReadUpdater) {
                    authorReqs.push(
                        userService.fetchUserById(domain.updatedBy, { muteErrors: true }).then((user) => {
                            if (user) {
                                domain.updatedByUsername = user.name
                            }
                        }),
                    )
                }

                await Promise.all(authorReqs)
            }

            setState((prevState) => ({ ...prevState, domain }))
        }
    }, [props.domainId, domainService, appState, userService])

    // Effects
    useEffect(() => {
        if (props.domainId) {
            fetchRequestedDomain().then(() => {
                const historyState = props.history.location.state
                if (historyState?.containerId) {
                    let element = document.getElementById(historyState.containerId)
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth' })
                    }
                }
            })
        }
    }, [props.domainId, props.history.location.state, fetchRequestedDomain])

    return (
        <Flex vertical gap={VERTICAL_WELL_GAP} className={getClassNames('ddf', props.className)}>
            <ConfigProvider
                theme={{
                    components: {
                        Form: {
                            itemMarginBottom: 0,
                            labelColor: 'var(--ant-color-secondary)',
                        },
                    },
                }}
            >
                <DomainDetailsWell loading={isLoading} domain={state.domain!} onDomainUpdated={handleDomainUpdated} />

                {appState.currentUser?.isInternalUser && (
                    <DomainServerWell
                        loading={isLoading}
                        domain={state.domain!}
                        onDomainUpdated={handleDomainUpdated}
                    />
                )}

                <SendIntegrationsWell
                    loading={isLoading}
                    domain={state.domain!}
                    onDomainUpdated={handleDomainUpdated}
                />

                <PlatformPreferencesWell
                    loading={isLoading}
                    domainId={props.domainId!}
                    deliveryChannels={state.domain?.activeChannels ?? [DeliveryChannel.WEB]}
                    preferences={userContext.preferences.state.bundle!}
                    onPreferencesUpdated={userContext.preferences.refresh}
                    userCanUpdatePlatformPreferences={appState.abilityStore.can(
                        AbilityAction.UPDATE,
                        asCaslSubject(SubjectEntity.DOMAIN, {
                            id: props.domainId!,
                            statusId: 1,
                        }),
                    )}
                />
            </ConfigProvider>
        </Flex>
    )
}

export default withRouter(DomainDataForm)
